import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './RancangDesain.css';
import { toast } from "react-toastify";
import html2canvas from 'html2canvas';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';
import apiClient, {
  getAuthToken,
  getCurrentUser,
  clearAuthSession,
  resolveMediaAssetUrl,
} from '../services/api';
import { ListState } from '../components/ListState';

/** Path statis (CRA homepage / PUBLIC_URL), untuk file di public/ */
function publicAssetPath(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}${p}`;
}

/** Musik dari public/audio dilayani di origin frontend; file upload memakai path di API. */
function resolveMusicAssetUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }
  if (url.startsWith('/audio')) return publicAssetPath(url);
  return resolveMediaAssetUrl(url);
}

function displayNameFromMusicUrl(url) {
  if (!url) return '';
  try {
    const pathPart = url.replace(/^https?:\/\/[^/?#]+/i, '');
    const last = pathPart.split('/').filter(Boolean).pop() || pathPart;
    const decoded = decodeURIComponent(last);
    return decoded.replace(/\.(mp3|wav|ogg|m4a|webm|aac|flac|mpeg)$/i, '').replace(/[-_]+/g, ' ').trim() || decoded;
  } catch {
    return url;
  }
}

/** Ekstensi dari URL path (untuk cek format). */
function extFromMediaUrl(url) {
  if (!url) return '';
  const base = String(url).split('?')[0].split('#')[0];
  const part = base.split('.').pop() || '';
  return part.toLowerCase();
}

function isJpegBytes(u8) {
  return u8 instanceof Uint8Array && u8.length > 2 && u8[0] === 0xff && u8[1] === 0xd8;
}

/** Unduh bytes dengan JWT + retry (media di API sering membutuhkan Authorization). */
async function fetchToBytesWithAuth(url, { retries = 2 } = {}) {
  const token = getAuthToken();
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return new Uint8Array(await res.arrayBuffer());
    } catch (e) {
      lastErr = e;
      if (attempt < retries) await new Promise((r) => setTimeout(r, 350 * (attempt + 1)));
    }
  }
  throw lastErr;
}

/**
 * Dekode raster yang didukung browser → JPEG untuk FFmpeg (WebP, dll.).
 */
async function rasterBytesToJpegForFfmpeg(bytes) {
  const blob = new Blob([bytes]);
  const url = URL.createObjectURL(blob);
  let bmp = null;
  try {
    try {
      bmp = await createImageBitmap(blob);
    } catch {
      /* lanjut ke Image */
    }
    let w;
    let h;
    let draw;
    if (bmp) {
      w = bmp.width;
      h = bmp.height;
      draw = (ctx, tw, th) => {
        ctx.drawImage(bmp, 0, 0, tw, th);
      };
    } else {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = () =>
          rej(new Error('Browser tidak bisa mendekode gambar (coba PNG/JPEG atau unggah ulang).'));
        img.src = url;
      });
      w = img.naturalWidth;
      h = img.naturalHeight;
      if (!w || !h) throw new Error('Ukuran gambar tidak valid');
      draw = (ctx, tw, th) => {
        ctx.drawImage(img, 0, 0, tw, th);
      };
    }
    const maxSide = 2560;
    let tw = w;
    let th = h;
    if (Math.max(w, h) > maxSide) {
      const s = maxSide / Math.max(w, h);
      tw = Math.round(w * s);
      th = Math.round(h * s);
    }
    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, tw, th);
    draw(ctx, tw, th);
    if (bmp) bmp.close();
    const outBlob = await new Promise((res, rej) => {
      canvas.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/jpeg', 0.92);
    });
    return new Uint8Array(await outBlob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Durasi efektif elemen audio/video (detik) setelah metadata siap (untuk sinkron play mode).
 */
async function mediaDurationSec(el) {
  if (!el) return 0;
  if (Number.isFinite(el.duration) && el.duration > 0) return el.duration;
  await new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      el.removeEventListener('loadedmetadata', finish);
      el.removeEventListener('error', finish);
      resolve();
    };
    el.addEventListener('loadedmetadata', finish);
    el.addEventListener('error', finish);
    setTimeout(finish, 3000);
  });
  return Number.isFinite(el.duration) && el.duration > 0 ? el.duration : 0;
}

/** Label tahap unduh video (dialog progres). */
const VIDEO_PHASE_LABEL = {
  init: 'Persiapan',
  fetch: 'Unduh aset',
  normalize: 'Siapkan gambar',
  encode: 'Render scene',
  concat: 'Gabung video',
  audio: 'Susun audio',
  mux: 'Mux akhir',
};

function normalizeMusicTrack(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const trackUrl = typeof raw.url === 'string' ? raw.url.trim() : '';
  if (!trackUrl) return null;
  const name = (typeof raw.name === 'string' && raw.name.trim()) || displayNameFromMusicUrl(trackUrl);
  return { name, url: trackUrl };
}

const emptyBoard = () => ({ 
  id: crypto.randomUUID(), 
  mediaUrl: '', 
  mediaName: '', 
  mediaType: 'image', // 'image' atau 'video'
  audioUrl: '', 
  audioName: '', 
  musicUrl: '', // Musik khusus untuk scene ini
  musicName: '',
  musicVolume: 100, // Volume musik per scene (0-100%)
  text: '', 
  durationSec: 3 
});

// Komponen Recorder
function Recorder({ onSave }) {
  const [recording, setRecording] = React.useState(false);
  const [mediaRecorder, setMediaRecorder] = React.useState(null);
  const chunks = React.useRef([]);

  async function startRec() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => chunks.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        chunks.current = [];
        const url = URL.createObjectURL(blob);
        onSave(blob, url);
      };
      rec.start();
      setMediaRecorder(rec);
      setRecording(true);
    } catch (err) {
      toast.error('Gagal akses mikrofon: ' + err.message);
    }
  }

  function stopRec() {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setRecording(false);
    }
  }

  return (
    <div className="recorder-container">
      {!recording ? (
        <button className="record-btn start" onClick={startRec}>
          <span className="record-icon">🎙️</span>
          Mulai Rekam
        </button>
      ) : (
        <button className="record-btn stop" onClick={stopRec}>
          <span className="record-icon">⏹️</span>
          Stop Rekam
          <div className="pulse-animation"></div>
        </button>
      )}
    </div>
  );
}

export default function Development() {
  const navigate = useNavigate();

  // ===== Identitas user yang login
  const [me, setMe] = useState(() => getCurrentUser());
  const [serverViewerRole, setServerViewerRole] = useState(null);
  useEffect(() => {
    setMe(getCurrentUser());
  }, []);
  const viewerIsGuru = me?.role === 'guru' || serverViewerRole === 'guru';

  // ===== State utama
  const [title, setTitle] = useState('Storyboard Baru');
  const [storyboards, setStoryboards] = useState([emptyBoard()]);
  const [currentBoard, setCurrentBoard] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [, setStopPlayback] = useState(false);

  // Pemilik storyboard yang sedang dibuka (null = project baru / belum disimpan).
  const [currentOwner, setCurrentOwner] = useState(null);
  // Flag dari backend: true kalau viewer adalah pemilik (boleh edit/save).
  // Saat membuat project baru selalu boleh.
  const [canEditCurrent, setCanEditCurrent] = useState(true);
  // Mode read-only aktif kalau guru sedang melihat karya siswa lain.
  const readOnly = !canEditCurrent;

  // ===== Refs audio
  const audioRefs = useRef([]);
  const musicRefs = useRef([]); // Ref untuk musik per scene
  /** Play mode: flag stop & unblock Promise tunggu scene (state stopPlayback stale di async loop). */
  const playbackStopRef = useRef(false);
  const playbackAdvanceRef = useRef(null);
  /** Play mode: video tiap scene (di-loop selama durasi scene). */
  const playbackVideoRefs = useRef([]);

  // ===== CRUD list
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total]);
  const [searchQ, setSearchQ] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterKelas, setFilterKelas] = useState('');
  const [filterSekolah, setFilterSekolah] = useState('');
  const [appliedKelas, setAppliedKelas] = useState('');
  const [appliedSekolah, setAppliedSekolah] = useState('');
  const [filterOptions, setFilterOptions] = useState({ kelas: [], sekolah: [] });
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState('');

  const [currentId, setCurrentId] = useState(null);
  const [saving, setSaving] = useState(false);

  // ===== Music state
  const [musicList, setMusicList] = useState([]);
  const [previewing, setPreviewing] = useState(null);
  const previewRef = useRef(null);

  // ===== Video Download State
  /** Modal progres unduh video: null | { progress, label, detail, warnings, phaseKey, sceneIndex, sceneCount } */
  const [videoDownloadModal, setVideoDownloadModal] = useState(null);
  const isDownloadingVideo = Boolean(videoDownloadModal);
  const downloadProgress = videoDownloadModal?.progress ?? 0;
  const ffmpegRef = useRef(null);

  // ===== Toast error handler
  const showErr = useCallback((msg) => {
    toast.error(msg);
  }, []);

  // ===== Helper function untuk stop semua audio
  const stopAllAudio = useCallback(() => {
    audioRefs.current.forEach(audio => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    musicRefs.current.forEach(music => {
      if (music) {
        music.pause();
        music.currentTime = 0;
      }
    });
    playbackVideoRefs.current.forEach((v) => {
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    });

    // Clear timeout jika ada
    if (window.currentPlaybackTimeout) {
      clearTimeout(window.currentPlaybackTimeout);
      window.currentPlaybackTimeout = null;
    }
    const advance = playbackAdvanceRef.current;
    if (advance) {
      playbackAdvanceRef.current = null;
      advance();
    }
  }, []);

  // Play mode: putar video scene aktif (loop); jeda video lain — klip pendek tidak “berhenti” di tengah scene
  useEffect(() => {
    if (!isPlaying) {
      playbackVideoRefs.current.forEach((el) => {
        if (el) {
          el.pause();
          el.currentTime = 0;
        }
      });
      return;
    }
    playbackVideoRefs.current.forEach((el, idx) => {
      if (!el) return;
      el.loop = true;
      el.muted = true;
      if (idx === currentBoard) {
        el.currentTime = 0;
        el.play().catch(() => {});
      } else {
        el.pause();
        el.currentTime = 0;
      }
    });
  }, [isPlaying, currentBoard]);

  // ===== Effects
  useEffect(() => {
    let cancelled = false;

    async function loadMusicList() {
      const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      const manifestUrl = `${base}/audio/music-manifest.json`;

      const [manifestTracks, apiTracks] = await Promise.all([
        fetch(manifestUrl)
          .then((r) => (r.ok ? r.json() : []))
          .then((data) => (Array.isArray(data) ? data : []))
          .catch(() => []),
        apiClient.get('/music').then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
      ]);

      if (cancelled) return;

      const map = new Map();
      for (const raw of manifestTracks) {
        const t = normalizeMusicTrack(raw);
        if (t) map.set(t.url, t);
      }
      for (const raw of apiTracks) {
        const t = normalizeMusicTrack(raw);
        if (t && !map.has(t.url)) map.set(t.url, t);
      }

      const merged = [...map.values()].sort((a, b) =>
        a.name.localeCompare(b.name, 'id', { sensitivity: 'base' })
      );
      setMusicList(merged);
    }

    loadMusicList();
    return () => {
      cancelled = true;
    };
  }, []);

  // ===== Inisialisasi FFmpeg
  useEffect(() => {
    const loadFFmpeg = async () => {
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      const ffmpeg = new FFmpeg();
      ffmpegRef.current = ffmpeg;

      try {
        await ffmpeg.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
      } catch (error) {
        console.error('Failed to load FFmpeg:', error);
      }
    };

    loadFFmpeg();
  }, []);

  // ===== Upload helper
  async function uploadFile(file) {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post('/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }

  // ===== Handlers: board file
  async function handleFileUpload(kind, idx, file) {
    try {
      const data = await uploadFile(file);
      setStoryboards(prev => prev.map((b, i) => {
        if (i !== idx) return b;
        
        switch (kind) {
          case 'media':
            const isVideo = file.type.startsWith('video/');
            return { 
              ...b, 
              mediaUrl: data.url, 
              mediaName: data.name,
              mediaType: isVideo ? 'video' : 'image'
            };
          case 'audio':
            return { ...b, audioUrl: data.url, audioName: data.name };
          case 'music':
            return { ...b, musicUrl: data.url, musicName: data.name };
          default:
            return b;
        }
      }));
      
      toast.success('File berhasil diupload!');
    } catch (e) {
      showErr('Gagal upload file: ' + (e.response?.data?.error || e.message));
    }
  }

  // ===== Board operations
  function addBoard() { 
    setStoryboards(prev => [...prev, emptyBoard()]);
    toast.success('Storyboard baru ditambahkan!');
  }
  
  function deleteBoard(idx) {
    setStoryboards(prev => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
    if (currentBoard >= storyboards.length - 1) setCurrentBoard(0);
    toast.info('Storyboard dihapus');
  }
  
  function moveBoard(idx, dir) {
    const j = idx + dir;
    if (j < 0 || j >= storyboards.length) return;
    const arr = [...storyboards];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setStoryboards(arr);
  }

  // ===== Play sequence dengan musik per scene
  async function play() {
    if (!storyboards?.length) return;
    setIsPlaying(true);
    setStopPlayback(false);
    playbackStopRef.current = false;
    playbackAdvanceRef.current = null;

    for (let i = 0; i < storyboards.length; i++) {
      if (playbackStopRef.current) {
        break;
      }

      setCurrentBoard(i);
      const board = storyboards[i];
      const audio = audioRefs.current[i];
      const music = musicRefs.current[i];

      // Durasi minimum dari pengaturan scene (visual / slot waktu)
      const configuredSec = Math.max(0.5, Number(board.durationSec) || 3);

      // Panjang narasi & musik (jika ada) — narasi utama; musik memperpanjang scene hanya tanpa narasi
      const [narrSec, musSec] = await Promise.all([
        audio && board.audioUrl ? mediaDurationSec(audio) : Promise.resolve(0),
        music && board.musicUrl ? mediaDurationSec(music) : Promise.resolve(0),
      ]);

      let dwellSec = configuredSec;
      if (board.audioUrl && narrSec > 0) {
        dwellSec = Math.max(dwellSec, narrSec);
      }
      if (board.musicUrl && musSec > 0 && !(board.audioUrl && narrSec > 0)) {
        dwellSec = Math.max(dwellSec, musSec);
      }
      const dwellMs = Math.round(dwellSec * 1000);

      // Mainkan musik scene jika ada dengan volume control
      if (music && board.musicUrl) {
        music.currentTime = 0;
        music.volume = (board.musicVolume || 100) / 100; // Apply volume (0-1)
        music.play().catch((err) => console.error('Music play error:', err));
      }

      // Mainkan audio narasi jika ada
      if (audio && board.audioUrl) {
        audio.currentTime = 0;
        audio.play().catch((err) => console.error('Audio play error:', err));
      }

      await new Promise((resolve) => {
        let done = false;
        const complete = () => {
          if (done) return;
          done = true;
          if (window.currentPlaybackTimeout === timeoutId) {
            window.currentPlaybackTimeout = null;
          }
          playbackAdvanceRef.current = null;
          resolve();
        };
        const timeoutId = setTimeout(complete, dwellMs);
        window.currentPlaybackTimeout = timeoutId;
        playbackAdvanceRef.current = () => {
          clearTimeout(timeoutId);
          complete();
        };
      });

      if (playbackStopRef.current) {
        break;
      }

      if (music) {
        music.pause();
        music.currentTime = 0;
      }
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    }

    playbackAdvanceRef.current = null;
    stopAllAudio();
    setIsPlaying(false);
    setCurrentBoard(0);
    setStopPlayback(false);
    playbackStopRef.current = false;
  }

  // ===================================================================
  // VIDEO DOWNLOAD — versi cepat & audio normal-volume.
  //
  // Optimasi performa:
  //   1. Pre-fetch SEMUA media (image/video/narasi/musik) PARALLEL via Promise.all
  //   2. `-preset ultrafast -tune stillimage` untuk image scenes (3-5x lebih cepat)
  //   3. Frame rate 24fps (cukup untuk slideshow, encode lebih ringan)
  //   4. Skip html2canvas kalau scene cuma image tanpa text → langsung pad image
  //   5. Concat pakai stream-copy (`-c copy`) — instant, tanpa re-encode
  //   6. JPEG (q=0.9) bukan PNG → encode html2canvas lebih cepat
  //
  // Fix audio (sebelumnya audio sangat pelan):
  //   - `amix` default `normalize=1` membagi semua track dengan jumlah input.
  //     Pakai `normalize=0` agar volume tiap track tetap utuh.
  //   - Narasi di-boost `volume=2.5` (mic recording biasanya pelan)
  //   - Musik base `volume = (slider/100) * 0.5` (slider 100% = 50% volume)
  //     supaya tidak menutupi narasi tapi tetap kedengeran jelas.
  //   - Final mux: pakai `-t totalVideoDuration` (jumlah durasi scene), JANGAN `-shortest`.
  //     `-shortest` memotong ke stream TERPENDEK → audio mix sering lebih pendek dari video
  //     gabungan, sehingga video terpotong / scene akhir hilang.
  // ===================================================================
  const downloadVideo = async () => {
    if (!ffmpegRef.current || !ffmpegRef.current.loaded) {
      toast.error('FFmpeg belum siap. Silakan tunggu beberapa saat.');
      return;
    }
    if (storyboards.length === 0) {
      toast.error('Tidak ada scene untuk dibuat video!');
      return;
    }

    const nScenes = storyboards.length;
    const t0 = performance.now();
    let closeDelayMs = 2000;

    const patchModal = (partial) => {
      setVideoDownloadModal((prev) => (prev ? { ...prev, ...partial } : prev));
    };
    const appendWarn = (msg) => {
      setVideoDownloadModal((prev) => (prev ? { ...prev, warnings: [...(prev.warnings || []), msg] } : prev));
    };

    setVideoDownloadModal({
      progress: 2,
      phaseKey: 'init',
      label: 'Mengunduh video',
      detail: `Menyiapkan ${nScenes} scene…`,
      warnings: [],
      sceneIndex: 0,
      sceneCount: nScenes,
    });
    await new Promise((r) => {
      requestAnimationFrame(() => requestAnimationFrame(r));
    });

    const ffmpeg = ffmpegRef.current;
    const FPS = 24;
    const VIDEO_W = 1280;
    const VIDEO_H = 720;
    const PAD_FILTER = `scale=${VIDEO_W}:${VIDEO_H}:force_original_aspect_ratio=decrease,pad=${VIDEO_W}:${VIDEO_H}:(ow-iw)/2:(oh-ih)/2:color=0x1a1a2e,setsar=1`;
    const COMMON_VIDEO_ARGS = [
      '-c:v', 'libx264',
      '-pix_fmt', 'yuv420p',
      '-preset', 'ultrafast',
      '-crf', '26',
      '-r', String(FPS),
      '-g', String(FPS * 2),
    ];

    /** Sama persis dengan durasi tiap scene di encode video (jangan beda dengan delay audio). */
    const sceneDurSec = (b) => Math.max(0.5, Number(b?.durationSec) || 3);
    const totalVideoDurationSec = storyboards.reduce((s, b) => s + sceneDurSec(b), 0);

    const resolveMediaUrl = (u) => resolveMediaAssetUrl(u);

    try {
      // ---------- PHASE 1: Pre-fetch SEMUA media (parallel + auth) ----------
      const fetchTasks = [];
      const mediaBytes = new Array(storyboards.length).fill(null);
      const narrBytes = new Array(storyboards.length).fill(null);
      const musicBytes = new Array(storyboards.length).fill(null);

      storyboards.forEach((b, i) => {
        if (b.mediaUrl) {
          fetchTasks.push(
            fetchToBytesWithAuth(resolveMediaUrl(b.mediaUrl))
              .then((bytes) => { mediaBytes[i] = bytes; })
              .catch((e) => {
                console.warn(`Media scene ${i + 1} gagal:`, e.message);
                appendWarn(`Scene ${i + 1}: media tidak terunduh (${e.message}). Pastikan Anda login dan URL valid.`);
              })
          );
        }
        if (b.audioUrl) {
          fetchTasks.push(
            fetchToBytesWithAuth(resolveMediaUrl(b.audioUrl))
              .then((bytes) => { narrBytes[i] = bytes; })
              .catch((e) => {
                console.warn(`Narasi scene ${i + 1} gagal:`, e.message);
                appendWarn(`Scene ${i + 1}: narasi tidak terunduh (${e.message}).`);
              })
          );
        }
        if (b.musicUrl) {
          fetchTasks.push(
            fetchToBytesWithAuth(resolveMusicAssetUrl(b.musicUrl))
              .then((bytes) => { musicBytes[i] = bytes; })
              .catch((e) => {
                console.warn(`Musik scene ${i + 1} gagal:`, e.message);
                appendWarn(`Scene ${i + 1}: musik tidak terunduh (${e.message}).`);
              })
          );
        }
      });

      patchModal({
        progress: 8,
        phaseKey: 'fetch',
        label: 'Mengunduh aset',
        detail: `Mengambil media, narasi, dan musik untuk ${nScenes} scene…`,
        sceneIndex: 0,
      });

      await Promise.all(fetchTasks);
      patchModal({ progress: 18, detail: 'Memeriksa format dan menyiapkan gambar…' });

      // ---------- NORMALIZE: gambar statis → JPEG di browser (WebP/AVIF/decode FFmpeg) ----------
      for (let i = 0; i < storyboards.length; i++) {
        const b = storyboards[i];
        if (b.mediaType === 'video' || !mediaBytes[i] || !b.mediaUrl) continue;
        const ext = extFromMediaUrl(b.mediaUrl);
        if (ext === 'svg') {
          appendWarn(
            `Scene ${i + 1}: SVG sering bermasalah di export video; bila kosong, unggah PNG atau JPEG.`
          );
        }
        if (['heic', 'heif'].includes(ext)) {
          appendWarn(
            `Scene ${i + 1}: HEIC/HEIF sering tidak didukung browser — jika gagal, ekspor ke JPEG dulu.`
          );
        }
        patchModal({
          progress: 18 + Math.round(((i + 1) / nScenes) * 10),
          phaseKey: 'normalize',
          label: 'Menyiapkan gambar',
          detail: `Konversi gambar scene ${i + 1} / ${nScenes}`,
          sceneIndex: i + 1,
        });
        try {
          mediaBytes[i] = await rasterBytesToJpegForFfmpeg(mediaBytes[i]);
        } catch (e) {
          appendWarn(
            `Scene ${i + 1}: gagal normalisasi JPEG (${e.message}). Dipakai berkas asli — encode FFmpeg bisa gagal.`
          );
        }
      }

      patchModal({
        progress: 30,
        phaseKey: 'encode',
        label: 'Mengolah scene',
        detail: `Encode ${nScenes} scene ke video…`,
      });

      // ---------- PHASE 2: Bikin video tiap scene ----------
      const scenes = [];
      for (let i = 0; i < storyboards.length; i++) {
        const board = storyboards[i];
        const duration = sceneDurSec(board);
        const sceneFile = `scene_${i}.mp4`;
        const isVideo = board.mediaType === 'video' && board.mediaUrl && mediaBytes[i];
        const isImage = board.mediaType !== 'video' && board.mediaUrl && mediaBytes[i];
        const hasText = !!(board.text && String(board.text).trim());

        const sceneKind = isVideo
          ? 'clip video'
          : isImage && !hasText
            ? 'gambar'
            : hasText
              ? 'gambar + teks (komposit)'
              : 'teks / tanpa media';

        patchModal({
          progress: 30 + Math.round(((i + 1) / nScenes) * 28),
          phaseKey: 'encode',
          label: `Scene ${i + 1} dari ${nScenes}`,
          detail: `Memproses: ${sceneKind}`,
          sceneIndex: i + 1,
        });

        try {
          if (isVideo) {
            const ext = (board.mediaUrl.split('.').pop() || 'mp4').toLowerCase().split('?')[0];
            const riskyV = ['mkv', 'mov', 'wmv', 'flv', 'avi', 'webm'];
            if (riskyV.includes(ext)) {
              appendWarn(
                `Scene ${i + 1}: video .${ext} bisa tidak didukung FFmpeg.wasm — bila gagal, gunakan MP4 (H.264 + AAC).`
              );
            }
            const inFile = `video_${i}.${ext}`;
            await ffmpeg.writeFile(inFile, mediaBytes[i]);
            await ffmpeg.exec([
              '-stream_loop', '-1',
              '-i', inFile,
              '-t', String(duration),
              '-vf', PAD_FILTER,
              '-an',
              ...COMMON_VIDEO_ARGS,
              '-y', sceneFile,
            ]);
          } else if (isImage && !hasText) {
            const ext = isJpegBytes(mediaBytes[i])
              ? 'jpg'
              : (board.mediaUrl.split('.').pop() || 'jpg').toLowerCase().split('?')[0];
            const inFile = `img_${i}.${ext}`;
            await ffmpeg.writeFile(inFile, mediaBytes[i]);
            try {
              await ffmpeg.exec([
                '-loop', '1',
                '-i', inFile,
                '-t', String(duration),
                '-vf', PAD_FILTER,
                '-tune', 'stillimage',
                ...COMMON_VIDEO_ARGS,
                '-y', sceneFile,
              ]);
            } catch (encErr) {
              console.warn(`Image encode retry scene ${i + 1}:`, encErr);
              appendWarn(`Scene ${i + 1}: encode gambar langsung gagal, mencoba ulang sebagai JPEG.`);
              const jpeg = await rasterBytesToJpegForFfmpeg(mediaBytes[i]);
              await ffmpeg.writeFile(`img_${i}.jpg`, jpeg);
              await ffmpeg.exec([
                '-loop', '1',
                '-i', `img_${i}.jpg`,
                '-t', String(duration),
                '-vf', PAD_FILTER,
                '-tune', 'stillimage',
                ...COMMON_VIDEO_ARGS,
                '-y', sceneFile,
              ]);
            }
          } else {
            await processImageSceneCompact(ffmpeg, i, board, duration, mediaBytes[i], {
              VIDEO_W, VIDEO_H, PAD_FILTER, COMMON_VIDEO_ARGS,
            });
          }
          scenes.push(sceneFile);
        } catch (err) {
          console.error(`Scene ${i + 1} gagal:`, err);
          appendWarn(`Scene ${i + 1} gagal (${err.message}) — dipakai slide hitam.`);
          toast.warning(`Scene ${i + 1} gagal, dipakai fallback hitam`);
          await ffmpeg.exec([
            '-f', 'lavfi',
            '-i', `color=c=0x1a1a2e:s=${VIDEO_W}x${VIDEO_H}:d=${duration}:r=${FPS}`,
            ...COMMON_VIDEO_ARGS,
            '-y', sceneFile,
          ]);
          scenes.push(sceneFile);
        }
      }

      // ---------- PHASE 3: Concat (stream copy → instant) ----------
      patchModal({
        progress: 62,
        phaseKey: 'concat',
        label: 'Menggabungkan video',
        detail: 'Menyatukan semua scene…',
      });
      const concatContent = scenes.map((s) => `file '${s}'`).join('\n') + '\n';
      await ffmpeg.writeFile('concat_list.txt', concatContent);
      try {
        await ffmpeg.exec([
          '-f', 'concat', '-safe', '0',
          '-i', 'concat_list.txt',
          '-c', 'copy',
          '-y', 'concatenated.mp4',
        ]);
      } catch (e) {
        console.warn('Stream-copy concat gagal, fallback re-encode:', e.message);
        appendWarn('Gabungan stream-copy gagal — melakukan re-encode (lebih lama).');
        await ffmpeg.exec([
          '-f', 'concat', '-safe', '0',
          '-i', 'concat_list.txt',
          ...COMMON_VIDEO_ARGS,
          '-y', 'concatenated.mp4',
        ]);
      }
      patchModal({ progress: 68, detail: 'Gabungan scene selesai.' });

      // ---------- PHASE 4: Susun audio tracks ----------
      patchModal({
        progress: 72,
        phaseKey: 'audio',
        label: 'Menyusun audio',
        detail: 'Menyiapkan narasi dan musik…',
      });
      const audioInputs = [];
      const filterParts = [];
      const mixLabels = [];
      let inputIdx = 0; // 0 = concatenated.mp4

      // Narration tracks — boost 2.5x
      for (let i = 0; i < storyboards.length; i++) {
        if (!narrBytes[i]) continue;
        const ext = (storyboards[i].audioUrl.split('.').pop() || 'mp3').toLowerCase().split('?')[0];
        const fname = `narr_${i}.${ext}`;
        if (!['mp3', 'wav', 'aac', 'm4a', 'ogg', 'opus', 'webm', 'flac'].includes(ext)) {
          appendWarn(`Scene ${i + 1}: narasi .${ext} mungkin tidak didukung — sebaiknya MP3/WAV.`);
        }
        await ffmpeg.writeFile(fname, narrBytes[i]);
        audioInputs.push('-i', fname);
        inputIdx++;

        let delayMs = 0;
        for (let j = 0; j < i; j++) delayMs += Math.round(sceneDurSec(storyboards[j]) * 1000);

        const lbl = `n${i}`;
        filterParts.push(
          `[${inputIdx}:a]aresample=44100,adelay=${delayMs}|${delayMs},volume=2.5[${lbl}]`
        );
        mixLabels.push(`[${lbl}]`);
      }

      // Music tracks — base 0.5 dikali slider, biar narasi tetap menonjol
      for (let i = 0; i < storyboards.length; i++) {
        if (!musicBytes[i]) continue;
        const ext = (storyboards[i].musicUrl.split('.').pop() || 'mp3').toLowerCase().split('?')[0];
        const fname = `mus_${i}.${ext}`;
        if (!['mp3', 'wav', 'aac', 'm4a', 'ogg', 'opus', 'webm', 'flac'].includes(ext)) {
          appendWarn(`Scene ${i + 1}: musik .${ext} mungkin tidak didukung — sebaiknya MP3.`);
        }
        await ffmpeg.writeFile(fname, musicBytes[i]);
        audioInputs.push('-i', fname);
        inputIdx++;

        let delayMs = 0;
        for (let j = 0; j < i; j++) delayMs += Math.round(sceneDurSec(storyboards[j]) * 1000);

        const userVol = Math.max(0, Math.min(150, Number(storyboards[i].musicVolume ?? 100)));
        const vol = (userVol / 100) * 0.5;

        const lbl = `m${i}`;
        filterParts.push(
          `[${inputIdx}:a]aresample=44100,adelay=${delayMs}|${delayMs},volume=${vol.toFixed(3)}[${lbl}]`
        );
        mixLabels.push(`[${lbl}]`);
      }

      patchModal({ progress: 82, detail: 'Audio siap untuk mux.' });

      // ---------- PHASE 5: Final mux ----------
      patchModal({
        progress: 86,
        phaseKey: 'mux',
        label: 'Mux akhir',
        detail: 'Menggabungkan video dan audio…',
      });
      const command = ['-i', 'concatenated.mp4', ...audioInputs];

      if (mixLabels.length === 0) {
        // No audio at all
        command.push('-c', 'copy', '-y', 'final_output.mp4');
      } else if (mixLabels.length === 1) {
        // Skip amix — cuma 1 track
        const onlyLabel = mixLabels[0].replace(/[[\]]/g, '');
        const filter = filterParts.join(';') + `;[${onlyLabel}]alimiter=limit=0.95[aout]`;
        command.push(
          '-filter_complex', filter,
          '-map', '0:v', '-map', '[aout]',
          '-c:v', 'copy',
          '-c:a', 'aac', '-b:a', '192k',
          '-t', String(totalVideoDurationSec),
          '-y', 'final_output.mp4'
        );
      } else {
        // Multi-track: normalize=0 supaya tidak auto-attenuate, lalu limiter
        const filter =
          filterParts.join(';') +
          `;${mixLabels.join('')}amix=inputs=${mixLabels.length}:duration=longest:normalize=0,alimiter=limit=0.95[aout]`;
        command.push(
          '-filter_complex', filter,
          '-map', '0:v', '-map', '[aout]',
          '-c:v', 'copy',
          '-c:a', 'aac', '-b:a', '192k',
          '-t', String(totalVideoDurationSec),
          '-y', 'final_output.mp4'
        );
      }

      console.log('FFmpeg final command:', command);
      await ffmpeg.exec(command);
      patchModal({ progress: 93, label: 'Mux akhir', detail: 'Menulis file MP4…' });

      // ---------- PHASE 6: Download ----------
      const data = await ffmpeg.readFile('final_output.mp4');
      if (!data || data.length === 0) throw new Error('Video output kosong');

      const videoBlob = new Blob([data], { type: 'video/mp4' });
      const url = URL.createObjectURL(videoBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

      const dt = ((performance.now() - t0) / 1000).toFixed(1);
      patchModal({
        progress: 100,
        label: 'Selesai',
        detail: `Unduhan dimulai — waktu proses ${dt} detik`,
      });
      toast.success(`✅ Video selesai dalam ${dt}s`);
      closeDelayMs = 2600;
    } catch (error) {
      console.error('Error generating video:', error);
      patchModal({
        progress: 100,
        label: 'Gagal',
        detail: error.message || String(error),
      });
      toast.error(`❌ Gagal membuat video: ${error.message}`);
      closeDelayMs = 4500;
    } finally {
      setTimeout(() => setVideoDownloadModal(null), closeDelayMs);
    }
  };

  // Composite scene (image + text) via html2canvas, lalu encode dengan stillimage tune.
  // Dipanggil HANYA kalau scene perlu render text overlay; image-only di-skip lewat path
  // langsung di downloadVideo() di atas.
async function processImageSceneCompact(ffmpeg, index, board, duration, mediaBytesParam, opts) {
  const { VIDEO_W, VIDEO_H, PAD_FILTER, COMMON_VIDEO_ARGS } = opts;
  let slideDiv = null;
  try {
    slideDiv = document.createElement('div');
    Object.assign(slideDiv.style, {
      width: `${VIDEO_W}px`,
      height: `${VIDEO_H}px`,
      backgroundColor: '#1a1a2e',
      padding: '40px',
      position: 'fixed',
      left: '-99999px',
      top: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      overflow: 'hidden',
      fontFamily: 'Arial, sans-serif',
      color: 'white',
    });

    if (board.mediaUrl && mediaBytesParam) {
      const blob = new Blob([mediaBytesParam]);
      const objUrl = URL.createObjectURL(blob);
      const img = document.createElement('img');
      img.src = objUrl;
      img.style.maxWidth = '72%';
      img.style.maxHeight = board.text ? '50%' : '88%';
      img.style.objectFit = 'contain';
      img.style.borderRadius = '8px';
      img.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
      const loadOnce = new Promise((resolve, reject) => {
        img.onload = async () => {
          try {
            if (img.decode) await img.decode();
          } catch (_) {
            /* decode opsional */
          }
          if (img.naturalWidth < 1) reject(new Error('Gambar tidak valid'));
          else resolve();
        };
        img.onerror = () => reject(new Error('Gagal memuat gambar'));
        img.src = objUrl;
      });
      await Promise.race([
        loadOnce,
        new Promise((_, rej) =>
          setTimeout(() => rej(new Error('Timeout memuat gambar (15 detik)')), 15000)
        ),
      ]);
      slideDiv.appendChild(img);
      // revoke setelah html2canvas render (deferred)
      setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
    }

    if (board.text) {
      const textDiv = document.createElement('div');
      const raw = String(board.text).trim();
      const fontSize =
        raw.length > 200 ? '20px' : raw.length > 120 ? '24px' : raw.length > 70 ? '26px' : '28px';
      Object.assign(textDiv.style, {
        color: 'white',
        fontSize,
        marginTop: '24px',
        textAlign: 'center',
        padding: '0 48px',
        lineHeight: '1.45',
        textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
        maxWidth: '92%',
        maxHeight: board.mediaUrl && mediaBytesParam ? '38%' : '72%',
        overflow: 'hidden',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        hyphens: 'auto',
        boxSizing: 'border-box',
        flexShrink: 0,
      });
      textDiv.textContent = board.text;
      slideDiv.appendChild(textDiv);
    }

    document.body.appendChild(slideDiv);
    const canvas = await html2canvas(slideDiv, {
      scale: 1,
      useCORS: true,
      backgroundColor: '#1a1a2e',
      allowTaint: true,
      logging: false,
      width: VIDEO_W,
      height: VIDEO_H,
      windowWidth: VIDEO_W,
      windowHeight: VIDEO_H,
    });
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
    if (!blob) throw new Error('canvas.toBlob returned null');

    const imageData = new Uint8Array(await blob.arrayBuffer());
    await ffmpeg.writeFile(`frame_${index}.jpg`, imageData);
    await ffmpeg.exec([
      '-loop', '1',
      '-i', `frame_${index}.jpg`,
      '-t', String(duration),
      '-vf', PAD_FILTER,
      '-tune', 'stillimage',
      ...COMMON_VIDEO_ARGS,
      '-y', `scene_${index}.mp4`,
    ]);
  } finally {
    if (slideDiv && slideDiv.parentNode) slideDiv.parentNode.removeChild(slideDiv);
  }
}

  // ===== CRUD operations
  const fetchList = useCallback(async (p = 1, q = '', filters = {}) => {
    const token = getAuthToken();
    if (!token) {
      setListError('Anda belum login. Silakan masuk terlebih dahulu.');
      setItems([]);
      setTotal(0);
      showErr('Anda belum login. Silakan masuk terlebih dahulu.');
      setTimeout(() => {
        clearAuthSession();
        navigate('/login', { replace: true });
      }, 800);
      return;
    }

    setLoadingList(true);
    setListError('');
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(limit) });
      if (q) params.set('q', q);
      if (filters.kelas) params.set('kelas', filters.kelas);
      if (filters.sekolah) params.set('sekolah', filters.sekolah);
      const { data } = await apiClient.get(`/storyboards?${params.toString()}`);
      setItems(data?.data || []);
      setTotal(data?.pagination?.total || 0);
      if (data?.meta?.viewerRole) {
        setServerViewerRole(data.meta.viewerRole);
      }
      if (data?.meta?.filterOptions) {
        setFilterOptions(data.meta.filterOptions);
      }
    } catch (e) {
      const status = e.response?.status;
      setItems([]);
      setTotal(0);
      if (status === 401) {
        const msg = e.response?.data?.message || 'Sesi habis. Silakan login ulang.';
        setListError(msg);
        showErr(msg);
        clearAuthSession();
        setTimeout(() => navigate('/login', { replace: true }), 800);
      } else if (!e.response) {
        const msg = 'Tidak dapat terhubung ke server. Coba refresh halaman atau login ulang.';
        setListError(msg);
        showErr(msg);
      } else {
        const msg = e.response?.data?.error || e.response?.data?.message || 'Gagal ambil daftar storyboard';
        setListError(msg);
        showErr(msg);
      }
    } finally {
      setLoadingList(false);
    }
  }, [limit, showErr, navigate]);

  const listFilters = useMemo(
    () => ({ kelas: appliedKelas, sekolah: appliedSekolah }),
    [appliedKelas, appliedSekolah]
  );

  async function saveNew() {
    if (readOnly) {
      showErr('Mode hanya-baca: Anda tidak dapat menyimpan karya milik orang lain.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title,
        boards: storyboards.map((b, idx) => ({
          imageUrl: b.mediaUrl,
          imageName: b.mediaName,
          mediaType: b.mediaType,
          audioUrl: b.audioUrl, 
          audioName: b.audioName,
          musicUrl: b.musicUrl,
          musicName: b.musicName,
          musicVolume: b.musicVolume || 100,
          text: b.text, 
          durationSec: b.durationSec || 3,
          order: idx
        }))
      };
      const { data } = await apiClient.post('/storyboards', payload);
      setCurrentId(data?.data?._id || null);
      setCurrentOwner({
        id: me?.id,
        nama: me?.nama,
        email: me?.email,
        role: me?.role,
      });
      setCanEditCurrent(true);
      toast.success('🎉 Storyboard berhasil disimpan!');
      fetchList(page, appliedSearch, listFilters);
    } catch (e) {
      showErr(e.response?.data?.error || e.message || 'Gagal menyimpan');
    } finally { setSaving(false); }
  }

  async function updateCurrent() {
    if (!currentId) return saveNew();
    if (readOnly) {
      showErr('Mode hanya-baca: Anda tidak dapat menyimpan karya milik orang lain.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title,
        boards: storyboards.map((b, idx) => ({
          imageUrl: b.mediaUrl,
          imageName: b.mediaName,
          mediaType: b.mediaType,
          audioUrl: b.audioUrl, 
          audioName: b.audioName,
          musicUrl: b.musicUrl,
          musicName: b.musicName,
          musicVolume: b.musicVolume || 100,
          text: b.text, 
          durationSec: b.durationSec || 3,
          order: idx
        }))
      };
      await apiClient.put(`/storyboards/${currentId}`, payload);
      toast.success('✅ Perubahan berhasil disimpan!');
      fetchList(page, appliedSearch, listFilters);
    } catch (e) {
      showErr(e.response?.data?.error || e.message || 'Gagal update');
    } finally { setSaving(false); }
  }

  async function loadOne(id) {
    try {
      const { data } = await apiClient.get(`/storyboards/${id}`);
      const sb = data?.data;
      if (!sb) return;
      setCurrentId(sb._id);
      setCurrentOwner(sb.owner || null);
      setCanEditCurrent(Boolean(data?.meta?.canEdit));
      setTitle(sb.title || 'Storyboard');
      setStoryboards((sb.boards || []).map(b => ({
        id: crypto.randomUUID(),
        mediaUrl: b.imageUrl || '',
        mediaName: b.imageName || '',
        mediaType: b.mediaType || 'image',
        audioUrl: b.audioUrl || '', 
        audioName: b.audioName || '',
        musicUrl: b.musicUrl || '',
        musicName: b.musicName || '',
        musicVolume: b.musicVolume || 100,
        text: b.text || '', 
        durationSec: b.durationSec || 3
      })));
      setIsPlaying(false);
      setCurrentBoard(0);
      if (sb.owner && sb.owner.id !== me?.id) {
        toast.info(`Mode hanya-baca: melihat karya ${sb.owner.nama || 'siswa'}`);
      } else {
        toast.success('Storyboard berhasil dimuat!');
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) {
        showErr('Anda tidak memiliki akses ke storyboard ini.');
      } else {
        showErr('Gagal memuat storyboard');
      }
    }
  }

  async function deleteOne(id) {
    if (!window.confirm('Hapus storyboard ini?')) return;
    try {
      await apiClient.delete(`/storyboards/${id}`);
      toast.success('🗑️ Storyboard berhasil dihapus!');
      if (currentId === id) {
        setCurrentId(null);
        setCurrentOwner(null);
        setCanEditCurrent(true);
        setTitle('Storyboard Baru');
        setStoryboards([emptyBoard()]);
        setIsPlaying(false);
      }
      fetchList(page, appliedSearch, listFilters);
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) {
        showErr('Anda tidak boleh menghapus karya milik orang lain.');
      } else {
        showErr('Gagal hapus');
      }
    }
  }

  function runSearch() {
    setPage(1);
    setAppliedSearch(searchQ.trim());
    setAppliedKelas(filterKelas);
    setAppliedSekolah(filterSekolah);
  }

  function clearFilters() {
    setSearchQ('');
    setAppliedSearch('');
    setFilterKelas('');
    setFilterSekolah('');
    setAppliedKelas('');
    setAppliedSekolah('');
    setPage(1);
  }

  useEffect(() => {
    fetchList(page, appliedSearch, listFilters);
  }, [page, appliedSearch, listFilters, fetchList]);

  return (
    <div className={`dev-container ${isPlaying ? 'playing-mode' : ''}`}>
      {/* Header Section */}
      <div className="dev-header">
        <div className="header-content">
          <h1>Studio Storyboard Kreatif</h1>
          <p>Buat cerita visual menakjubkan dengan gambar, video, audio, dan teks!</p>
        </div>
      </div>

      {/* Controls Header */}
      <div className="controls-header">
        <div className="title-section">
          <input
            className="title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Masukkan judul storyboard..."
            readOnly={readOnly}
            disabled={readOnly}
          />
          <div className="title-actions">
            {readOnly && currentOwner && (
              <span
                className="readonly-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: '#fef3c7',
                  color: '#92400e',
                  fontWeight: 600,
                  fontSize: 13,
                  border: '1px solid #fde68a',
                }}
                title={`Karya milik ${currentOwner.nama || 'siswa'} ${currentOwner.kelas ? '(' + currentOwner.kelas + ')' : ''}`}
              >
                👁️ Mode Lihat — karya {currentOwner.nama || 'siswa'}
              </span>
            )}
            <button
              className="btn new-btn"
              onClick={() => {
                setCurrentId(null);
                setCurrentOwner(null);
                setCanEditCurrent(true);
                setTitle('Storyboard Baru');
                setStoryboards([emptyBoard()]);
                toast.success('Project baru dibuat!');
              }}
            >
              🆕 Project Baru
            </button>
            <button
              className="btn save-btn"
              disabled={saving || readOnly}
              onClick={currentId ? updateCurrent : saveNew}
              title={readOnly ? 'Tidak dapat menyimpan karya milik orang lain' : ''}
            >
              {saving ? '💾 Menyimpan...' : currentId ? '✏️ Update Project' : '💾 Simpan Project'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <div className="stat-value">{items.length}</div>
            <div className="stat-label">Total Project</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎞️</div>
          <div className="stat-info">
            <div className="stat-value">{storyboards.length}</div>
            <div className="stat-label">Scene Aktif</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎵</div>
          <div className="stat-info">
            <div className="stat-value">
              {storyboards.filter(b => b.musicUrl).length}
            </div>
            <div className="stat-label">Scene dengan Musik</div>
          </div>
        </div>
      </div>

      {!isPlaying ? (
        <>
          {/* Main Content Grid */}
          <section className="dev-grid">
            {/* Left Panel - Music Library & Projects */}
            <div className="left-panel">
              {/* Music Library */}
              <div className="music-library-card">
                <div className="card-header">
                  <h2>🎵 Library Musik</h2>
                  <div className="card-badge">Pilih untuk Scene</div>
                </div>
                
                <div className="music-library">
                  {musicList.length === 0 ? (
                    <p className="music-library-empty">
                      Belum ada musik di <code>public/audio</code>. Tambahkan file audio (.mp3, .wav, …) lalu jalankan{' '}
                <code>npm start</code> atau <code>npm run build</code> (manifest diperbarui otomatis). Musik dari API juga muncul di sini jika tersedia.
                    </p>
                  ) : null}
                  {musicList.map((m) => (
                    <div key={m.url} className="music-library-item">
                      <div className="music-info">
                        <span className="music-name">{m.name}</span>
                        <div className="music-library-actions">
                          <button
                            className="btn music-btn select"
                            disabled={readOnly}
                            onClick={() => {
                              if (readOnly) return;
                              const currentBoardData = storyboards[currentBoard];
                              if (currentBoardData) {
                                setStoryboards(prev => prev.map((b, i) => 
                                  i === currentBoard 
                                    ? { ...b, musicUrl: m.url, musicName: m.name }
                                    : b
                                ));
                                toast.success(`Musik "${m.name}" ditambahkan ke Scene ${currentBoard + 1}`);
                              }
                            }}
                            title={readOnly ? 'Mode hanya-baca' : 'Tambahkan ke scene aktif'}
                          >
                            ➕ Tambah ke Scene
                          </button>
                          <button
                            className="btn music-btn preview"
                            onClick={() => {
                              if (!previewRef.current) return;
                              if (previewing === m.url) {
                                previewRef.current.pause();
                                setPreviewing(null);
                              } else {
                                const musicSrc = resolveMusicAssetUrl(m.url);
                                previewRef.current.src = musicSrc;
                                previewRef.current.load();
                                previewRef.current.play().catch((err) => {
                                  console.error('Preview error:', err);
                                  toast.error('Tidak bisa memutar preview musik.');
                                  setPreviewing(null);
                                });
                                setPreviewing(m.url);
                              }
                            }}
                          >
                            {previewing === m.url ? '⏹️ Stop' : '▶️ Preview'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <audio ref={previewRef} playsInline preload="auto" onEnded={() => setPreviewing(null)} />
                </div>

                <div
                  className="music-upload-area"
                  style={readOnly ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                  title={readOnly ? 'Mode hanya-baca' : ''}
                >
                  <label className="upload-label">
                    <span>📁 Upload Musik Baru</span>
                    <input 
                      type="file" 
                      accept="audio/*" 
                      disabled={readOnly}
                      onChange={async (e) => {
                        if (readOnly) return;
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const data = await uploadFile(file);
                            const currentBoardData = storyboards[currentBoard];
                            if (currentBoardData) {
                              setStoryboards(prev => prev.map((b, i) => 
                                i === currentBoard 
                                  ? { ...b, musicUrl: data.url, musicName: data.name }
                                  : b
                              ));
                              toast.success('Musik berhasil diupload dan ditambahkan ke scene!');
                            }
                          } catch (error) {
                            showErr('Gagal upload musik');
                          }
                        }
                      }}
                    />
                  </label>
                  <p className="upload-hint">Musik akan ditambahkan ke scene yang sedang aktif</p>
                </div>
              </div>

              {/* Projects List */}
              <div className="projects-card">
                <div className="card-header">
                  <h2>
                    {viewerIsGuru ? '📂 Semua Karya Siswa' : '📂 Project Saya'}
                  </h2>
                  {viewerIsGuru && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: 999,
                        background: '#dcfce7',
                        color: '#166534',
                        fontSize: 12,
                        fontWeight: 600,
                        marginLeft: 8,
                      }}
                      title="Anda login sebagai guru — dapat melihat semua karya siswa (read-only)"
                    >
                      👨‍🏫 Mode Guru
                    </span>
                  )}
                  <div className="search-box">
                    <input
                      placeholder="🔍 Cari project..."
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') runSearch();
                      }}
                      className="search-input"
                    />
                    <button className="btn search-btn" onClick={runSearch}>
                      Cari
                    </button>
                    {(appliedSearch || appliedKelas || appliedSekolah) && (
                      <button
                        type="button"
                        className="btn search-btn"
                        onClick={clearFilters}
                        title="Hapus semua filter"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {viewerIsGuru && (
                    <div className="guru-filters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                      <select
                        value={filterKelas}
                        onChange={(e) => setFilterKelas(e.target.value)}
                        className="search-input"
                        style={{ minWidth: 140 }}
                      >
                        <option value="">Semua Kelas</option>
                        {(filterOptions.kelas || []).map((k) => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>
                      <select
                        value={filterSekolah}
                        onChange={(e) => setFilterSekolah(e.target.value)}
                        className="search-input"
                        style={{ minWidth: 180 }}
                      >
                        <option value="">Semua Sekolah</option>
                        {(filterOptions.sekolah || []).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <button type="button" className="btn search-btn" onClick={runSearch}>
                        Terapkan Filter
                      </button>
                    </div>
                  )}
                </div>

                <div className="projects-list">
                  <ListState
                    loading={loadingList}
                    error={listError || null}
                    empty={!loadingList && !listError && items.length === 0}
                    onRetry={() => fetchList(page, appliedSearch, listFilters)}
                    emptyProps={{
                      icon: '📝',
                      loadingMessage: 'Memuat project...',
                      title: viewerIsGuru
                        ? (appliedSearch || appliedKelas || appliedSekolah ? 'Tidak ada karya yang cocok' : 'Belum ada karya siswa')
                        : (appliedSearch ? 'Tidak ada project yang cocok' : 'Belum ada project'),
                      message: viewerIsGuru
                        ? (appliedSearch || appliedKelas || appliedSekolah
                          ? 'Coba filter lain atau hapus pencarian.'
                          : 'Karya siswa akan muncul di sini setelah mereka menyimpan storyboard.')
                        : (appliedSearch
                          ? 'Coba kata kunci lain atau hapus filter pencarian.'
                          : 'Buat project storyboard pertamamu!'),
                    }}
                  >
                    {items.map(it => {
                      const ownerId = it.owner?.id || it.userId || null;
                      const mineHere = me?.id && ownerId && String(ownerId) === String(me.id);
                      const ownerName = it.owner?.nama || (mineHere ? 'Anda' : 'Tanpa pemilik');
                      const ownerKelas = it.owner?.kelas || '';
                      const ownerRole = it.owner?.role || '';
                      return (
                        <div key={it._id} className={`project-item ${currentId === it._id ? 'active' : ''}`}>
                          <div className="project-avatar">
                            {it.boards?.[0]?.imageUrl ? (
                              <div className="media-preview">
                                {it.boards[0].mediaType === 'video' ? (
                                  <video src={resolveMediaAssetUrl(it.boards[0].imageUrl)} muted />
                                ) : (
                                  <img src={resolveMediaAssetUrl(it.boards[0].imageUrl)} alt="Preview" />
                                )}
                              </div>
                            ) : (
                              '🎬'
                            )}
                          </div>
                          <div className="project-info">
                            <div className="project-title">{it.title}</div>
                            {viewerIsGuru && (
                              <div
                                className="project-owner"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  marginTop: 4,
                                  fontSize: 12,
                                  color: '#475569',
                                  fontWeight: 500,
                                }}
                              >
                                <span
                                  style={{
                                    padding: '2px 8px',
                                    borderRadius: 999,
                                    background: mineHere ? '#dbeafe' : '#f1f5f9',
                                    color: mineHere ? '#1e40af' : '#334155',
                                    fontWeight: 600,
                                  }}
                                >
                                  {ownerRole === 'guru' ? '👨‍🏫' : '🎓'} {ownerName}
                                </span>
                                {ownerKelas && (
                                  <span style={{ color: '#64748b' }}>· {ownerKelas}</span>
                                )}
                              </div>
                            )}
                            <div className="project-meta">
                              <span>{it.boards?.length || 0} scene</span>
                              <span>•</span>
                              <span>{it.boards?.filter(b => b.musicUrl).length || 0} musik</span>
                              <span>•</span>
                              <span>{new Date(it.createdAt).toLocaleDateString('id-ID')}</span>
                            </div>
                          </div>
                          <div className="project-actions">
                            <button className="btn action-btn load" onClick={() => loadOne(it._id)}>
                              {mineHere ? '📂 Buka' : '👁️ Lihat'}
                            </button>
                            {mineHere && (
                              <button className="btn action-btn delete" onClick={() => deleteOne(it._id)}>
                                🗑️
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  }
                  </ListState>
                </div>

                {/* Pagination */}
                {items.length > 0 && (
                  <div className="pagination">
                    <button className="btn pagination-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      ◀️ Prev
                    </button>
                    <span className="pagination-info">Halaman {page} dari {totalPages}</span>
                    <button className="btn pagination-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      Next ▶️
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Storyboard Editor */}
            <div className={`right-panel ${readOnly ? 'readonly-mode' : ''}`}>
              {readOnly && currentOwner && (
                <div
                  style={{
                    margin: '0 0 12px',
                    padding: '10px 14px',
                    borderRadius: 10,
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    color: '#92400e',
                    fontSize: 13,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  👁️ <strong>Mode hanya-baca</strong> — Anda sedang melihat karya
                  <strong>&nbsp;{currentOwner.nama || 'siswa'}</strong>
                  {currentOwner.kelas ? ` (${currentOwner.kelas})` : ''}.
                  Editor dinonaktifkan, tapi Anda masih bisa Preview & Download Video.
                </div>
              )}
              <div className="editor-header">
                <h2>🎨 Editor Storyboard</h2>
                <div className="editor-actions">
                  <button
                    className="btn add-board-btn"
                    onClick={addBoard}
                    disabled={readOnly}
                    title={readOnly ? 'Mode hanya-baca' : ''}
                  >
                    ➕ Tambah Scene
                  </button>
                  <button className="btn preview-btn" onClick={() => setIsPlaying(true)}>
                    🎬 Preview Storyboard
                  </button>
                  <button 
                    className="btn download-video-btn" 
                    onClick={downloadVideo}
                    disabled={isDownloadingVideo}
                  >
                    {isDownloadingVideo ? (
                      <>
                        <span className="spinner"></span>
                        Membuat Video… {downloadProgress}%
                      </>
                    ) : (
                      '📥 Download Video'
                    )}
                  </button>
                </div>
              </div>

              <fieldset
                disabled={readOnly}
                style={{
                  border: 0,
                  padding: 0,
                  margin: 0,
                  minWidth: 0,
                  opacity: readOnly ? 0.85 : 1,
                }}
              >
              <div className="storyboards-grid">
                {storyboards.map((b, idx) => (
                  <div key={b.id} className="storyboard-card">
                    <div className="card-header">
                      <div className="scene-header">
                        <span className="scene-number">
                          {b.mediaType === 'video' ? '🎥' : '🎭'} Scene #{idx + 1}
                          {b.musicUrl && ' 🎵'}
                        </span>
                        <div className="scene-actions">
                          <button className="icon-btn move-up" onClick={() => moveBoard(idx, -1)} title="Pindah ke atas" disabled={readOnly}>
                            ⬆️
                          </button>
                          <button className="icon-btn move-down" onClick={() => moveBoard(idx, +1)} title="Pindah ke bawah" disabled={readOnly}>
                            ⬇️
                          </button>
                          <button className="icon-btn delete-scene" onClick={() => deleteBoard(idx)} title="Hapus scene" disabled={readOnly}>
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="scene-content">
                      {/* Media Type Selection */}
                      <div className="media-type-section">
                        <label className="section-label">🎬 Jenis Media:</label>
                        <div className="media-type-buttons">
                          <button
                            className={`media-type-btn ${b.mediaType === 'image' ? 'active' : ''}`}
                            onClick={() => setStoryboards(prev => prev.map((x, i) => 
                              i === idx ? { ...x, mediaType: 'image' } : x
                            ))}
                          >
                            🖼️ Gambar
                          </button>
                          <button
                            className={`media-type-btn ${b.mediaType === 'video' ? 'active' : ''}`}
                            onClick={() => setStoryboards(prev => prev.map((x, i) => 
                              i === idx ? { ...x, mediaType: 'video' } : x
                            ))}
                          >
                            🎥 Video
                          </button>
                        </div>
                      </div>

                      {/* Media Upload */}
                      <div className="upload-section">
                        <label className="upload-label media-upload">
                          <span className="upload-icon">
                            {b.mediaType === 'video' ? '🎥' : '🖼️'}
                          </span>
                          <span className="upload-text">
                            {b.mediaUrl ? `Ganti ${b.mediaType === 'video' ? 'Video' : 'Gambar'}` : 
                             `Upload ${b.mediaType === 'video' ? 'Video' : 'Gambar'}`}
                          </span>
                          <input 
                            type="file" 
                            accept={b.mediaType === 'video' ? 'video/*' : 'image/*'} 
                            hidden 
                            onChange={(e) => e.target.files?.[0] && handleFileUpload('media', idx, e.target.files[0])}
                          />
                        </label>
                        
                        {b.mediaUrl && (
                          <div className="media-preview">
                            {b.mediaType === 'video' ? (
                              <div className="video-preview">
                                <video controls loop playsInline muted={false}>
                                  <source src={resolveMediaAssetUrl(b.mediaUrl)} />
                                  Browser tidak mendukung video.
                                </video>
                                <div className="media-overlay">
                                  <span>🎥 Video</span>
                                </div>
                              </div>
                            ) : (
                              <div className="image-preview">
                                <img src={resolveMediaAssetUrl(b.mediaUrl)} alt="Preview" />
                                <div className="media-overlay">
                                  <span>🖼️ Gambar</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Scene Music */}
                      <div className="scene-music-section">
                        <label className="section-label">
                          🎵 Musik Scene {idx + 1}
                          {b.musicName && `: ${b.musicName}`}
                        </label>
                        
                        <div className="music-controls-row">
                          <div className="music-selection">
                            <select
                              className="music-select"
                              value={b.musicUrl || ''}
                              onChange={(e) => {
                                if (e.target.value) {
                                  const selectedMusic = musicList.find(m => m.url === e.target.value);
                                  if (selectedMusic) {
                                    setStoryboards(prev => prev.map((x, i) => 
                                      i === idx ? { ...x, musicUrl: selectedMusic.url, musicName: selectedMusic.name } : x
                                    ));
                                  }
                                } else {
                                  setStoryboards(prev => prev.map((x, i) => 
                                    i === idx ? { ...x, musicUrl: '', musicName: '' } : x
                                  ));
                                }
                              }}
                            >
                              <option value="">-- Pilih Musik --</option>
                              {musicList.map((m) => (
                                <option key={m.url} value={m.url}>
                                  {m.name}
                                </option>
                              ))}
                            </select>
                            
                            <label className="upload-label small">
                              <span>📤 Upload</span>
                              <input 
                                type="file" 
                                accept="audio/*" 
                                hidden 
                                onChange={(e) => e.target.files?.[0] && handleFileUpload('music', idx, e.target.files[0])}
                              />
                            </label>
                            
                            {b.musicUrl && (
                              <button 
                                className="btn remove-btn"
                                onClick={() => setStoryboards(prev => prev.map((x, i) => 
                                  i === idx ? { ...x, musicUrl: '', musicName: '' } : x
                                ))}
                              >
                                ❌ Hapus
                              </button>
                            )}
                          </div>
                          
                          {b.musicUrl && (
                            <div className="music-preview">
                              <audio
                                ref={(el) => (musicRefs.current[idx] = el)}
                                src={resolveMusicAssetUrl(b.musicUrl)}
                                controls
                                playsInline
                                preload="metadata"
                              />
                            </div>
                          )}
                        </div>

                        {/* Volume Control untuk Musik */}
                        {b.musicUrl && (
                          <div className="music-volume-control">
                            <label className="volume-label">
                              🔊 Volume Musik: <span className="volume-value">{b.musicVolume}%</span>
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={b.musicVolume}
                              onChange={(e) => setStoryboards(prev => prev.map((x, i) => 
                                i === idx ? { ...x, musicVolume: parseInt(e.target.value) } : x
                              ))}
                              className="volume-slider"
                            />
                            <div className="volume-presets">
                              <button 
                                className="preset-btn" 
                                onClick={() => setStoryboards(prev => prev.map((x, i) => 
                                  i === idx ? { ...x, musicVolume: 25 } : x
                                ))}
                              >
                                25%
                              </button>
                              <button 
                                className="preset-btn" 
                                onClick={() => setStoryboards(prev => prev.map((x, i) => 
                                  i === idx ? { ...x, musicVolume: 50 } : x
                                ))}
                              >
                                50%
                              </button>
                              <button 
                                className="preset-btn" 
                                onClick={() => setStoryboards(prev => prev.map((x, i) => 
                                  i === idx ? { ...x, musicVolume: 75 } : x
                                ))}
                              >
                                75%
                              </button>
                              <button 
                                className="preset-btn" 
                                onClick={() => setStoryboards(prev => prev.map((x, i) => 
                                  i === idx ? { ...x, musicVolume: 100 } : x
                                ))}
                              >
                                100%
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Audio Narasi Upload & Record */}
                      <div className="audio-section">
                        <div className="audio-upload">
                          <label className="upload-label audio-upload-btn">
                            <span className="upload-icon">🎤</span>
                            <span className="upload-text">
                              {b.audioUrl ? 'Ganti Narasi' : 'Upload Narasi'}
                            </span>
                            <input 
                              type="file" 
                              accept="audio/*" 
                              hidden
                              onChange={(e) => e.target.files?.[0] && handleFileUpload('audio', idx, e.target.files[0])}
                            />
                          </label>
                          
                          <Recorder
                            onSave={(blob, url) => {
                              setStoryboards(prev => prev.map((x, i) =>
                                i === idx ? { ...x, audioUrl: url, audioName: 'rekaman.webm' } : x
                              ));
                              const file = new File([blob], "rekaman.webm", { type: "audio/webm" });
                              handleFileUpload('audio', idx, file);
                            }}
                          />
                        </div>

                        {b.audioUrl && (
                          <div className="audio-player">
                            <audio
                              ref={(el) => (audioRefs.current[idx] = el)}
                              src={b.audioUrl.startsWith('/audio') ? publicAssetPath(b.audioUrl) : resolveMediaAssetUrl(b.audioUrl)}
                              controls
                            />
                          </div>
                        )}
                      </div>

                      {/* Text Input */}
                      <div className="text-section">
                        <label className="section-label">📝 Teks Cerita</label>
                        <textarea
                          value={b.text}
                          onChange={(e) => setStoryboards(prev => prev.map((x, i) => 
                            i === idx ? { ...x, text: e.target.value } : x
                          ))}
                          placeholder="Tulis narasi atau dialog untuk scene ini..."
                          className="story-textarea"
                        />
                      </div>

                      {/* Duration Control */}
                      <div className="duration-section">
                        <label className="section-label">⏱️ Durasi Scene (detik)</label>
                        <input
                          type="number"
                          min={1}
                          max={60}
                          value={b.durationSec}
                          onChange={(e) => setStoryboards(prev => prev.map((x, i) => 
                            i === idx ? { ...x, durationSec: Number(e.target.value) || 1 } : x
                          ))}
                          className="duration-input"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </fieldset>

            </div>
          </section>
        </>
      ) : (
        // ===== PLAY MODE =====
        <section className="play-mode">
          <div className="play-header">
            <h2>🎬 Memutar Storyboard: {title}</h2>
            <div className="play-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${((currentBoard + 1) / storyboards.length) * 100}%` 
                  }}
                ></div>
              </div>
              <span>Scene {currentBoard + 1} dari {storyboards.length}</span>
            </div>
          </div>

          <div className="play-content">
            {storyboards.map((b, idx) => (
              <div key={b.id} className={`play-slide ${idx === currentBoard ? 'active' : ''}`}>
                {b.mediaUrl && (
                  <div className="slide-media">
                    {b.mediaType === 'video' ? (
                      <video
                        ref={(el) => {
                          playbackVideoRefs.current[idx] = el;
                        }}
                        muted
                        loop
                        playsInline
                        preload="auto"
                        src={resolveMediaAssetUrl(b.mediaUrl)}
                        onEnded={(e) => {
                          const v = e.currentTarget;
                          if (idx === currentBoard && isPlaying) {
                            v.currentTime = 0;
                            v.play().catch(() => {});
                          }
                        }}
                      />
                    ) : (
                      <img src={resolveMediaAssetUrl(b.mediaUrl)} alt="Visual story" />
                    )}
                  </div>
                )}
                {b.text && (
                  <div className="slide-caption">
                    <div className="caption-text">{b.text}</div>
                  </div>
                )}
                <audio 
                  ref={(el) => (audioRefs.current[idx] = el)} 
                  src={b.audioUrl ? resolveMediaAssetUrl(b.audioUrl) : undefined} 
                />
                <audio 
                  ref={(el) => (musicRefs.current[idx] = el)} 
                  src={b.musicUrl ? resolveMusicAssetUrl(b.musicUrl) : undefined}
                  playsInline
                  preload="auto"
                />
              </div>
            ))}
          </div>

          <div className="play-controls">
            <button className="btn stop-btn" onClick={() => { 
              playbackStopRef.current = true;
              setStopPlayback(true);
              setIsPlaying(false);
              stopAllAudio();
              setCurrentBoard(0);
            }}>
              ⏹️ Stop
            </button>
            <button className="btn play-btn" onClick={play}>
              ▶️ Play
            </button>
            <button 
              className="btn download-video-btn" 
              onClick={downloadVideo}
              disabled={isDownloadingVideo}
            >
              {isDownloadingVideo ? (
                <>
                  <span className="spinner"></span>
                  Membuat Video... {downloadProgress}%
                </>
              ) : (
                '📥 Download Video (MP4)'
              )}
            </button>
          </div>
        </section>
      )}

      {videoDownloadModal && (
        <div
          className="video-download-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="video-download-title"
        >
          <div className="video-download-dialog">
            <h3 id="video-download-title" className="video-download-title">
              {videoDownloadModal.label}
            </h3>
            <p className="video-download-detail">{videoDownloadModal.detail}</p>
            {videoDownloadModal.sceneCount > 0 && (
              <p className="video-download-scene">
                Scene {videoDownloadModal.sceneIndex || '—'} / {videoDownloadModal.sceneCount}
                {videoDownloadModal.phaseKey ? (
                  <span className="video-download-phase">
                    {' · '}
                    {VIDEO_PHASE_LABEL[videoDownloadModal.phaseKey] || videoDownloadModal.phaseKey}
                  </span>
                ) : null}
              </p>
            )}
            <div className="progress-bar video-download-progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${Math.min(100, videoDownloadModal.progress || 0)}%` }}
              />
            </div>
            <div className="video-download-pct">{Math.round(videoDownloadModal.progress || 0)}%</div>
            {videoDownloadModal.warnings?.length > 0 && (
              <div className="video-download-warnings-wrap">
                <div className="video-download-warnings-title">Catatan & format</div>
                <ul className="video-download-warnings">
                  {videoDownloadModal.warnings.map((w, wi) => (
                    <li key={wi}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="dev-footer">
        <div className="footer-content">
          <h3>🎉 Selamat Berkarya!</h3>
          <p>Setiap cerita dimulai dengan satu scene. Teruslah berimajinasi!</p>
        </div>
      </div>
    </div>
  );
}